import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useQueries, useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import useAuth from '../../auth/useAuth';
import { useSoundStatus } from '../../context/SoundStatusContext';
import { useTour } from '../../context/TourContext';

import Layout from '../UI/introGameLayout/layout';
import Modal from '../UI/modal';
import { gameQuestionsLimit } from './data';
import {
  gamesItemQuery,
  generalInfoQuery,
  postDecisionsBulk,
  updateGameSession,
} from './api';

import AnswerCorrectSound from './sounds/answer-correct.mp3';
import AnswerWrongSound from './sounds/answer-wrong.mp3';

import {
  getApcFrameworkIds,
  validateApcDecision,
  calculateScore,
  formatDecisionsForBulkPost,
} from './validation';

import './styles.css';

const DEFAULT_CATEGORIES = [
  { key: 'Assumptions', options: ['Economic Entity', 'Monetary Unit', 'Periodicity', 'Going Concern'] },
  { key: 'Principles',  options: ['Revenue Recognition', 'Expense Recognition', 'Historical Cost', 'Full Disclosure'] },
  { key: 'Constraints', options: ['Cost-Benefit', 'Materiality', 'Conservatism'] },
];

// Map of scenario-level name → reflection tour name. Levels listed here
// share the APC three-category layout; their categories and the central
// Select All card receive matching `data-tour-id` markers below.
const REFLECTION_TOURS_BY_LEVEL = {
  'assumptions-principles-constraints': 'apc-reflection',
};

// Map of category key → tour-id. Wired into renderCategory's <section>.
const APC_TOUR_IDS_BY_KEY = {
  Assumptions: 'apc-assumptions',
  Principles:  'apc-principles',
  Constraints: 'apc-constraints',
};

// Recognised category labels — used for case-insensitive matching when the
// backend returns options grouped by a `parents` label that may or may not
// match our exact casing.
const CATEGORY_KEYS = ['Assumptions', 'Principles', 'Constraints'];

const INCORRECT_FLASH_MS = 700;
const CARD_FLIP_DURATION_MS = 700;
const CARD_FLIP_SWAP_MS = 350;

const TOOLTIP_VIEWPORT_MARGIN = 12;
const TOOLTIP_GAP = 10;

function CategoryInfoTooltip({ description }) {
  const wrapperRef = useRef(null);
  const tooltipRef = useRef(null);
  const [positionStyle, setPositionStyle] = useState(null);
  const [placement, setPlacement] = useState('top');

  useLayoutEffect(() => {
    if (!description) return undefined;

    const compute = () => {
      const wrapperEl = wrapperRef.current;
      const tooltipEl = tooltipRef.current;
      if (!wrapperEl || !tooltipEl) return;

      const wrapperRect = wrapperEl.getBoundingClientRect();
      const tooltipRect = tooltipEl.getBoundingClientRect();
      const viewportW = window.innerWidth;

      const wrapperCenterX = wrapperRect.left + wrapperRect.width / 2;
      let left = wrapperCenterX - tooltipRect.width / 2;
      if (left < TOOLTIP_VIEWPORT_MARGIN) {
        left = TOOLTIP_VIEWPORT_MARGIN;
      }
      if (left + tooltipRect.width > viewportW - TOOLTIP_VIEWPORT_MARGIN) {
        left = viewportW - TOOLTIP_VIEWPORT_MARGIN - tooltipRect.width;
      }
      const leftRelativeToWrapper = left - wrapperRect.left;

      const fitsAbove = wrapperRect.top >= tooltipRect.height + TOOLTIP_GAP + TOOLTIP_VIEWPORT_MARGIN;

      setPositionStyle({
        left: `${leftRelativeToWrapper}px`,
        transform: 'none',
      });
      setPlacement(fitsAbove ? 'top' : 'bottom');
    };

    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [description]);

  return (
    <span
      ref={wrapperRef}
      className="apc-level-info-wrapper"
      tabIndex={description ? 0 : -1}
    >
      <span className="apc-level-info" aria-hidden="true">ⓘ</span>
      {description && (
        <div
          ref={tooltipRef}
          className={`apc-level-info-tooltip apc-level-info-tooltip--${placement}`}
          role="tooltip"
          style={positionStyle || undefined}
        >
          {description}
        </div>
      )}
    </span>
  );
}

function ApcOption({
  option,
  variant,
  isSelected,
  isFlashing,
  showPoints,
  pointsValue,
  onClick,
}) {
  const wrapperRef = useRef(null);
  const tooltipRef = useRef(null);
  const [positionStyle, setPositionStyle] = useState(null);
  const [placement, setPlacement] = useState('top');

  const description = option.description || null;

  useLayoutEffect(() => {
    if (!description) return undefined;

    const compute = () => {
      const wrapperEl = wrapperRef.current;
      const tooltipEl = tooltipRef.current;
      if (!wrapperEl || !tooltipEl) return;

      const wrapperRect = wrapperEl.getBoundingClientRect();
      const tooltipRect = tooltipEl.getBoundingClientRect();
      const viewportW = window.innerWidth;

      const wrapperCenterX = wrapperRect.left + wrapperRect.width / 2;
      let left = wrapperCenterX - tooltipRect.width / 2;
      if (left < TOOLTIP_VIEWPORT_MARGIN) {
        left = TOOLTIP_VIEWPORT_MARGIN;
      }
      if (left + tooltipRect.width > viewportW - TOOLTIP_VIEWPORT_MARGIN) {
        left = viewportW - TOOLTIP_VIEWPORT_MARGIN - tooltipRect.width;
      }
      const leftRelativeToWrapper = left - wrapperRect.left;

      const fitsAbove = wrapperRect.top >= tooltipRect.height + TOOLTIP_GAP + TOOLTIP_VIEWPORT_MARGIN;

      setPositionStyle({
        left: `${leftRelativeToWrapper}px`,
        transform: 'none',
      });
      setPlacement(fitsAbove ? 'top' : 'bottom');
    };

    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [description]);

  return (
    <li
      ref={wrapperRef}
      className={`apc-level-option-wrapper${description ? ' has-tooltip' : ''}`}
    >
      <button
        type="button"
        className={[
          'apc-level-option',
          `apc-level-option--${variant}`,
          isSelected ? 'selected' : '',
          isFlashing ? 'incorrect' : '',
        ].filter(Boolean).join(' ')}
        onClick={onClick}
        disabled={isSelected}
      >
        {option.name}
        {showPoints && (
          <span className="apc-level-points">+{pointsValue}</span>
        )}
      </button>
      {description && (
        <div
          ref={tooltipRef}
          className={`apc-level-option-tooltip apc-level-option-tooltip--${placement}`}
          role="tooltip"
          style={positionStyle || undefined}
        >
          <strong className="apc-level-option-tooltip-title">{option.name}</strong>
          <p className="apc-level-option-tooltip-body">{description}</p>
        </div>
      )}
    </li>
  );
}

export default function ThePrinciplesApcLevel({ levelNumber, scenarioLevels, answerData }) {
  const { startTour } = useTour();
  const { soundStatus } = useSoundStatus();
  const navigate = useNavigate();
  const { getUser, getCurrentThePrinciplesGameId } = useAuth();
  const user = getUser();
  const gameId = getCurrentThePrinciplesGameId();

  const scenarioLevel = useMemo(
    () => scenarioLevels?.find(level => level.level === levelNumber) || null,
    [scenarioLevels, levelNumber]
  );

  const transactions = scenarioLevel?.transactions || [];
  const totalQuestions = Math.min(
    gameQuestionsLimit,
    transactions.length || gameQuestionsLimit
  );

  const [activeTransaction, setActiveTransaction] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [incorrectlyFlashing, setIncorrectlyFlashing] = useState(() => new Set());
  const [hasMadeDecision, setHasMadeDecision] = useState(false);
  const [totalScore, setTotalScore] = useState(0);
  const [flashingPoints, setFlashingPoints] = useState(null);
  const [isFlippingCard, setIsFlippingCard] = useState(false);
  const [isWellDoneVisible, setIsWellDoneVisible] = useState(false);
  const [isSummaryButtonLoading, setIsSummaryButtonLoading] = useState(false);
  const [currentGameSessionId, setCurrentGameSessionId] = useState(0);

  const questionTriesRef = useRef({});
  const questionStartRef = useRef(Date.now());
  const gameStartRef = useRef(Date.now());
  const decisionsRef = useRef([]);
  const postInFlightRef = useRef(null);
  const audioCorrectRef = useRef(null);
  const audioWrongRef = useRef(null);
  const flipTimeoutsRef = useRef([]);

  const { data: generalInfo } = useQuery(generalInfoQuery());

  useEffect(() => {
    const games = generalInfo?.games;
    const gamesSessions = generalInfo?.sessions;
    if (!games || !gamesSessions || !gameId || !user?.id || currentGameSessionId) return;

    const currentGameTypeSessions = gamesSessions.filter(session =>
      games.find(g => g.id === session.game && g.game_type === 'The Principles')
    );
    const currentGameSessions = currentGameTypeSessions.filter(
      session => session.user === user.id && session.game === gameId
    );
    if (!currentGameSessions.length) return;

    const inProgress = currentGameSessions.find(s => !s.completed);
    if (inProgress) {
      setCurrentGameSessionId(inProgress.id);
      return;
    }
    const mostRecentCompleted = currentGameSessions
      .filter(s => s.completed)
      .reduce((max, s) => (s.id > (max?.id || 0) ? s : max), null);
    if (mostRecentCompleted) setCurrentGameSessionId(mostRecentCompleted.id);
  }, [generalInfo, gameId, user?.id, currentGameSessionId]);

  // Replay guard: if this level is already completed in the resolved
  // session (e.g. the player hit the back button after completing it),
  // skip past the gameplay component and forward them to the next level
  // — or to the summary if this was the last level. The Principles has
  // no points page; we mirror the post-completion flow used elsewhere.
  // Fires once per mount via a ref so it can't re-trigger after the
  // player completes this level mid-session.
  const replayGuardCheckedRef = useRef(false);
  useEffect(() => {
    if (replayGuardCheckedRef.current) return;
    const gamesSessions = generalInfo?.sessions;
    if (!gamesSessions || !currentGameSessionId || !scenarioLevels?.length) return;
    const session = gamesSessions.find(s => s.id === currentGameSessionId);
    if (!session) return;
    replayGuardCheckedRef.current = true;
    if (session[`level${levelNumber}`] === 'completed') {
      const isLastLevel = scenarioLevels.length === levelNumber;
      navigate(
        isLastLevel ? '/the-principles/summary' : `/the-principles/level/${levelNumber + 1}/`,
        { replace: true }
      );
    }
  }, [generalInfo, currentGameSessionId, levelNumber, scenarioLevels, navigate]);

  const updateGameSessionMutation = useMutation({
    mutationFn: (data) => updateGameSession(data),
  });

  // Preload sounds once.
  useEffect(() => {
    audioCorrectRef.current = new Audio(AnswerCorrectSound);
    audioWrongRef.current = new Audio(AnswerWrongSound);
    audioCorrectRef.current.preload = 'auto';
    audioWrongRef.current.preload = 'auto';
    return () => {
      audioCorrectRef.current?.pause();
      audioWrongRef.current?.pause();
    };
  }, []);

  // Clean up any pending flip timers on unmount.
  useEffect(() => {
    return () => {
      flipTimeoutsRef.current.forEach(clearTimeout);
      flipTimeoutsRef.current = [];
    };
  }, []);

  // Always render the three Figma-defined category boxes. We try to pull
  // option names from scenarioLevel.principles_options (grouped by a
  // case-insensitive match on account_item.parents) so the chips reflect
  // what the instructor configured; if a box ends up with nothing, we fall
  // back to the default Figma list so the UI is never empty.
  const categories = useMemo(() => {
    const optsForCategory = (categoryKey) => {
      const opts = scenarioLevel?.principles_options || [];
      const matched = opts
        .filter(opt => {
          const parentNames = (opt?.account_item?.parent_objects || [])
            .map(p => p?.name)
            .filter(Boolean);
          return parentNames.some(
            n => n.toLowerCase() === categoryKey.toLowerCase()
          );
        })
        .map(opt => ({
          accountItemId: opt?.account_item?.id ?? null,
          name: opt?.account_item?.name,
          description: opt?.account_item?.description || null,
        }))
        .filter(o => o.name);
      if (matched.length) return matched;
      const fallback = DEFAULT_CATEGORIES.find(c => c.key === categoryKey);
      return fallback ? fallback.options.map(name => ({ accountItemId: null, name, description: null })) : [];
    };

    return CATEGORY_KEYS.map(key => ({ key, options: optsForCategory(key) }));
  }, [scenarioLevel]);

  const findCategory = key => categories.find(c => c.key === key);
  const assumptions = findCategory('Assumptions');
  const principles  = findCategory('Principles');
  const constraints = findCategory('Constraints');

  // Stable key for Set tracking — prefers the account_item.id; falls back
  // to the option name only when the backend hasn't populated ids yet.
  const keyOf = (opt) => (opt.accountItemId != null ? opt.accountItemId : `name:${opt.name}`);

  // Map each category key to its parent account_item id (144/145/146 in
  // the current dataset). Parent is found on any option whose
  // parent_objects entry matches the category name (case-insensitive).
  const parentIdByCategory = useMemo(() => {
    const opts = scenarioLevel?.principles_options || [];
    const map = {};
    CATEGORY_KEYS.forEach(key => {
      for (const opt of opts) {
        const parent = (opt?.account_item?.parent_objects || []).find(
          p => typeof p?.name === 'string' && p.name.toLowerCase() === key.toLowerCase()
        );
        if (parent?.id != null) {
          map[key] = parent.id;
          break;
        }
      }
    });
    return map;
  }, [scenarioLevel]);

  // Fetch the parent account_item for each category so we can show its
  // `description` in a tooltip next to the category heading.
  const parentIdList = useMemo(
    () => CATEGORY_KEYS.map(k => parentIdByCategory[k]).filter(id => id != null),
    [parentIdByCategory]
  );
  const parentItemQueries = useQueries({
    queries: parentIdList.map(id => gamesItemQuery({ id })),
  });
  const parentItemsById = useMemo(() => {
    const map = new Map();
    parentItemQueries.forEach(q => {
      if (q?.data?.id != null) map.set(q.data.id, q.data);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentItemQueries.map(q => q?.data?.id).join(','), parentItemQueries.map(q => q?.data?.description).join('|')]);

  const categoryDescriptions = useMemo(() => {
    const map = {};
    CATEGORY_KEYS.forEach(key => {
      const pid = parentIdByCategory[key];
      const item = pid != null ? parentItemsById.get(pid) : null;
      if (item?.description) map[key] = item.description;
    });
    return map;
  }, [parentIdByCategory, parentItemsById]);

  // Slide-in animation gate — only on the first transaction, pre-decision.
  const shouldPlayIntroAnimation = !hasMadeDecision && activeTransaction === 0;

  const currentTransaction = transactions[activeTransaction];
  const currentQuestionId = currentTransaction?.id;
  const currentTransactionId = currentTransaction?.transaction_id;
  const progressPct = Math.min(
    100,
    Math.round((activeTransaction / Math.max(1, totalQuestions)) * 100)
  );

  const correctOptionIds = useMemo(
    () => getApcFrameworkIds(answerData, currentTransactionId),
    [answerData, currentTransactionId]
  );

  const allCorrectSelected = correctOptionIds.length > 0
    && correctOptionIds.every(id => selected.has(id));

  // Reset per-question state when moving to the next transaction.
  useEffect(() => {
    setSelected(new Set());
    setIncorrectlyFlashing(new Set());
    setFlashingPoints(null);
    questionStartRef.current = Date.now();
  }, [activeTransaction]);

  const playSound = (audioRef) => {
    if (soundStatus !== 'enabled' || !audioRef.current) return;
    try {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } catch { /* ignore */ }
  };

  const handleOptionClick = (option) => {
    const key = keyOf(option);
    // Block input while the card is mid-flip.
    if (isFlippingCard) return;
    // Once correctly selected, the chip is locked in — no-op.
    if (selected.has(key)) return;
    // Ignore clicks during the incorrect flash for the same option.
    if (incorrectlyFlashing.has(key)) return;

    const timeElapsedSec = Math.max(
      1,
      Math.round((Date.now() - questionStartRef.current) / 1000)
    );

    const tryKey = `${currentQuestionId}:${key}`;
    const tries = (questionTriesRef.current[tryKey] || 0) + 1;
    questionTriesRef.current[tryKey] = tries;

    const isCorrect = validateApcDecision(
      answerData,
      currentTransactionId,
      option.accountItemId
    );

    // Record every click — backend derives is_correct and accuracy from
    // selected_option_item vs. the question's framework, and its accuracy
    // metric is tries-based, so wrong attempts must be posted too.
    // game_time_elapsed = cumulative seconds since game-start, so the
    // bulk POST sends a value that grows monotonically across rows.
    if (option.accountItemId != null && currentQuestionId != null) {
      const gameTimeElapsedSec = Math.max(
        1,
        Math.round((Date.now() - gameStartRef.current) / 1000)
      );
      decisionsRef.current.push({
        game: gameId,
        game_session: currentGameSessionId,
        question: currentQuestionId,
        selected_option_item: option.accountItemId,
        time_elapsed: Math.max(1, timeElapsedSec),
        game_time_elapsed: gameTimeElapsedSec,
        timestamp: new Date().toISOString(),
      });
    }

    if (isCorrect) {
      const points = Math.round(
        calculateScore({ timeElapsedSeconds: timeElapsedSec, totalTries: tries })
      );
      setSelected(prev => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      setTotalScore(prev => prev + points);
      setFlashingPoints({ key, points });
      setTimeout(() => {
        setFlashingPoints(current =>
          current?.key === key ? null : current
        );
      }, 1200);
      playSound(audioCorrectRef);
    } else {
      setIncorrectlyFlashing(prev => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      setTimeout(() => {
        setIncorrectlyFlashing(prev => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }, INCORRECT_FLASH_MS);
      playSound(audioWrongRef);
    }
  };

  const isLastQuestion = activeTransaction >= totalQuestions - 1;

  const finishGameAndPostDecisions = () => {
    setIsWellDoneVisible(true);

    if (currentGameSessionId) {
      updateGameSessionMutation.mutate({
        id: currentGameSessionId,
        levelNumber,
        status: 'completed',
        isSessionCompleted: scenarioLevels?.length === levelNumber,
      });
    }

    const gameTimeElapsedSec = Math.max(
      1,
      Math.round((Date.now() - gameStartRef.current) / 1000)
    );
    const decisionsToPost = decisionsRef.current;

    if (decisionsToPost.length) {
      postInFlightRef.current = (async () => {
        try {
          const formatted = formatDecisionsForBulkPost(decisionsToPost, gameTimeElapsedSec);
          await postDecisionsBulk(formatted);
          decisionsRef.current = [];
        } catch (error) {
          console.error('Failed to post APC decisions:', error);
        }
      })();
    }
  };

  const handleContinue = () => {
    if (!allCorrectSelected || isFlippingCard) return;

    if (isLastQuestion) {
      setHasMadeDecision(true);
      finishGameAndPostDecisions();
      return;
    }

    setHasMadeDecision(true);
    setIsFlippingCard(true);
    // Swap the question content when the card is edge-on (half-way through flip).
    const swapTimeout = setTimeout(() => {
      setActiveTransaction(prev => prev + 1);
    }, CARD_FLIP_SWAP_MS);
    const endTimeout = setTimeout(() => {
      setIsFlippingCard(false);
    }, CARD_FLIP_DURATION_MS);
    flipTimeoutsRef.current.push(swapTimeout, endTimeout);
  };

  const handleViewSummaryClick = async () => {
    if (isSummaryButtonLoading) return;
    setIsSummaryButtonLoading(true);
    try {
      if (postInFlightRef.current) await postInFlightRef.current;
    } finally {
      // Per-level reflection tour. Only fires for levels in the registry;
      // navigates to the summary inside the tour's onComplete callback so
      // the user lands on the summary after closing the final step.
      const reflectionTour = REFLECTION_TOURS_BY_LEVEL[scenarioLevel?.name];
      if (reflectionTour) {
        setIsWellDoneVisible(false);
        startTour(reflectionTour, { onComplete: () => navigate('/the-principles/summary') });
        return;
      }
      navigate('/the-principles/summary');
    }
  };

  const animClass = (direction) => {
    if (!shouldPlayIntroAnimation) return '';
    return `apc-slide-in-${direction}`;
  };

  const renderCategory = (category, variant, direction, isHorizontal = false) => {
    if (!category) return null;
    const description = categoryDescriptions[category.key];
    const tourId = REFLECTION_TOURS_BY_LEVEL[scenarioLevel?.name]
      ? APC_TOUR_IDS_BY_KEY[category.key]
      : undefined;
    return (
      <section
        className={`apc-level-category apc-level-category--${variant} ${animClass(direction)}`}
        {...(tourId && { 'data-tour-id': tourId })}
      >
        <div className="apc-level-category-header">
          <h3>{category.key}</h3>
          <CategoryInfoTooltip description={description} />
        </div>
        <ul className={`apc-level-options ${isHorizontal ? 'horizontal' : ''}`}>
          {category.options.map(opt => {
            const key = keyOf(opt);
            const isSelected = selected.has(key);
            const isFlashing = incorrectlyFlashing.has(key);
            const showPoints = flashingPoints?.key === key;
            return (
              <ApcOption
                key={key}
                option={opt}
                variant={variant}
                isSelected={isSelected}
                isFlashing={isFlashing}
                showPoints={showPoints}
                pointsValue={flashingPoints?.points}
                onClick={() => handleOptionClick(opt)}
              />
            );
          })}
        </ul>
      </section>
    );
  };

  const isContinueDisabled = !allCorrectSelected;

  return (
    <Layout
      isHeaderVisible={true}
      totalScore={totalScore}
      scoreLabel="Points"
      className="apc-level-layout"
    >
      <div className="apc-level-wrapper">
        <div className="apc-level-progress">
          <div className="apc-level-progress-labels">
            <span>Progress</span>
            <span>Question {Math.min(totalQuestions, activeTransaction + 1)} of {totalQuestions}</span>
          </div>
          <div className="apc-level-progress-track">
            <div className="apc-level-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        <div className="apc-level-game">
          {renderCategory(assumptions, 'assumptions', 'left')}

          <div className="apc-level-center">
            <div
              className={`apc-level-card ${isFlippingCard ? 'flipping' : ''}`}
              {...(REFLECTION_TOURS_BY_LEVEL[scenarioLevel?.name] ? { 'data-tour-id': 'apc-select-all-card' } : {})}
            >
              <div className="apc-level-card-body">
                <h2 className="apc-level-card-title">
                  <span>Select All Applicable</span>
                  <span>Assumptions, Principles, and Constraints</span>
                </h2>
                {currentTransaction?.description && (
                  <p className="apc-level-transaction">{currentTransaction.description}</p>
                )}
              </div>
              <div className="apc-level-card-footer">
                <span className={`apc-level-continue-wrapper ${isContinueDisabled ? 'disabled' : ''}`}>
                  <button
                    type="button"
                    className={`apc-level-continue ${isContinueDisabled ? 'disabled' : ''}`}
                    onClick={handleContinue}
                    disabled={isContinueDisabled}
                  >
                    Continue
                  </button>
                  {isContinueDisabled && (
                    <span className="apc-level-continue-tooltip" role="tooltip">
                      Select All That Apply
                    </span>
                  )}
                </span>
              </div>
            </div>

            {renderCategory(principles, 'principles', 'bottom', true)}
          </div>

          {renderCategory(constraints, 'constraints', 'right')}
        </div>
      </div>

      {isWellDoneVisible && (
        <Modal
          content={
            <div className="modal-center vertical">
              <div><span>Well done!</span></div>
              <button
                onClick={handleViewSummaryClick}
                className={`button-blue button-view-summary ${isSummaryButtonLoading ? 'loading' : ''}`}
              >
                {!isSummaryButtonLoading && 'View Summary'}
              </button>
            </div>
          }
          isModalSmall={true}
        />
      )}
    </Layout>
  );
}
